import React, {
    useCallback,
    useEffect,
    useRef,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from "react";
import { Tag, TAG_TEXT_LABEL_SELECTOR } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import {
    DocumentIcon,
    InvoicingIcon,
    MembersIcon,
    SpreadsheetIcon,
    TransactionsIcon,
} from "@icons";
import { SpreadsheetReference } from "@common/schemas/spreadsheetReference.schema";
import {
    buildTransactionRefLabel,
    TransactionReference,
} from "@common/schemas/transactionReference.schema";
import {
    buildCounterpartyRefLabel,
    CounterpartyReference,
} from "@common/schemas/counterpartyReference.schema";
import {
    buildInvoiceRefLabel,
    InvoiceReference,
} from "@common/schemas/invoiceReference.schema";
import {
    buildDocumentRefLabel,
    DocumentReference,
} from "@common/schemas/documentReference.schema";
import { buildRangeLabel } from "@components/spreadsheet/spreadsheetUtils";
import { useTransactionRefContextMaybe } from "@components/tasks/TransactionRefContext";
import { useCounterpartyRefContextMaybe } from "@components/tasks/CounterpartyRefContext";
import { useInvoiceRefContextMaybe } from "@components/tasks/InvoiceRefContext";
import { useDocumentRefContextMaybe } from "@components/tasks/DocumentRefContext";
import { useSpreadsheetRefFocus } from "./useSpreadsheetRefFocus";
import styles from "./DraftRefTags.module.scss";

interface DraftRefTagProps<TRef> {
    reference: TRef;
    onRemove?: () => void;
}

const isLabelTruncated = (trigger: HTMLSpanElement) => {
    const label = trigger.querySelector<HTMLElement>(TAG_TEXT_LABEL_SELECTOR);
    return label !== null && isOverflowingX(label);
};

/**
 * Composer tag markup shared by every draft reference type. Long labels are
 * cut off in the narrow composer, so the full label shows on hover then.
 */
function DraftRefTagView({
    icon,
    label,
    onFocusReference,
    onRemove,
}: {
    icon: ReactNode;
    label: string;
    onFocusReference?: () => void;
    onRemove?: () => void;
}) {
    const triggerRef = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref: triggerRef,
        isTruncated: isLabelTruncated,
    });
    return (
        <Tooltip content={label} {...truncationTooltipProps}>
            <span ref={triggerRef} className={styles.trigger}>
                <Tag
                    size="md"
                    color="primary"
                    outline
                    leftIcon={icon}
                    onClick={onFocusReference}
                    onRemove={onRemove}
                    removeLabel={`Remove ${label}`}
                >
                    {label}
                </Tag>
            </span>
        </Tooltip>
    );
}

/**
 * Click handler that focuses `id` in its task ref context, or undefined without
 * one. A focus this tag set is cleared when the tag goes away (removed or sent),
 * unless another reference took it since.
 */
function useFocusByIdHandler<TId extends string | number>(
    id: TId,
    setFocusedId?: Dispatch<SetStateAction<TId | null>>,
) {
    const hasFocusedRef = useRef(false);
    const handleFocus = useCallback(() => {
        hasFocusedRef.current = true;
        setFocusedId?.(id);
    }, [setFocusedId, id]);
    useEffect(
        () => () => {
            if (hasFocusedRef.current) {
                setFocusedId?.((current) => (current === id ? null : current));
            }
        },
        [setFocusedId, id],
    );
    return setFocusedId ? handleFocus : undefined;
}

/** Draft cell-range tag; click highlights the cells or restores their saved view. */
export const DraftSpreadsheetRefTag = React.memo(
    function DraftSpreadsheetRefTag({
        reference,
        onRemove,
    }: DraftRefTagProps<SpreadsheetReference>) {
        const {
            isViewMismatch,
            canHighlight,
            highlight,
            clearHighlight,
            applyView,
        } = useSpreadsheetRefFocus(reference);
        const label = buildRangeLabel(reference.cells);
        const hasHighlightedRef = useRef(false);
        const handleHighlight = useCallback(() => {
            hasHighlightedRef.current = true;
            highlight();
        }, [highlight]);
        useEffect(
            () => () => {
                if (hasHighlightedRef.current) {
                    clearHighlight();
                }
            },
            [clearHighlight],
        );
        const handleFocus = isViewMismatch ? applyView : handleHighlight;
        return (
            <DraftRefTagView
                icon={<SpreadsheetIcon />}
                label={label}
                onFocusReference={
                    canHighlight || isViewMismatch ? handleFocus : undefined
                }
                onRemove={onRemove}
            />
        );
    },
);

/** Draft transaction tag; click focuses the row in the task table. */
export const DraftTransactionRefTag = React.memo(
    function DraftTransactionRefTag({
        reference,
        onRemove,
    }: DraftRefTagProps<TransactionReference>) {
        const refCtx = useTransactionRefContextMaybe();
        const handleFocus = useFocusByIdHandler(
            reference.transactionId,
            refCtx?.setFocusedTransactionId,
        );
        return (
            <DraftRefTagView
                icon={<TransactionsIcon />}
                label={reference.label ?? buildTransactionRefLabel(reference)}
                onFocusReference={handleFocus}
                onRemove={onRemove}
            />
        );
    },
);

/** Draft counterparty tag; click focuses the row in the task table. */
export const DraftCounterpartyRefTag = React.memo(
    function DraftCounterpartyRefTag({
        reference,
        onRemove,
    }: DraftRefTagProps<CounterpartyReference>) {
        const refCtx = useCounterpartyRefContextMaybe();
        const handleFocus = useFocusByIdHandler(
            reference.counterpartyId,
            refCtx?.setFocusedCounterpartyId,
        );
        return (
            <DraftRefTagView
                icon={<MembersIcon />}
                label={reference.label ?? buildCounterpartyRefLabel(reference)}
                onFocusReference={handleFocus}
                onRemove={onRemove}
            />
        );
    },
);

/** Draft invoice tag; click focuses the row in the task table. */
export const DraftInvoiceRefTag = React.memo(function DraftInvoiceRefTag({
    reference,
    onRemove,
}: DraftRefTagProps<InvoiceReference>) {
    const refCtx = useInvoiceRefContextMaybe();
    const handleFocus = useFocusByIdHandler(
        reference.invoiceId,
        refCtx?.setFocusedInvoiceId,
    );
    return (
        <DraftRefTagView
            icon={<InvoicingIcon />}
            label={reference.label ?? buildInvoiceRefLabel(reference)}
            onFocusReference={handleFocus}
            onRemove={onRemove}
        />
    );
});

/** Draft document tag; click focuses the row in the task table. */
export const DraftDocumentRefTag = React.memo(function DraftDocumentRefTag({
    reference,
    onRemove,
}: DraftRefTagProps<DocumentReference>) {
    const refCtx = useDocumentRefContextMaybe();
    const handleFocus = useFocusByIdHandler(
        reference.documentId,
        refCtx?.setFocusedDocumentId,
    );
    return (
        <DraftRefTagView
            icon={<DocumentIcon />}
            label={reference.label ?? buildDocumentRefLabel(reference)}
            onFocusReference={handleFocus}
            onRemove={onRemove}
        />
    );
});
